/**
 * @Description 百炼平台智能体
 */

import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useMethods } from '@lhb/hook';
import { isArray, isEqual } from '@lhb/func';
import { getStorage, setStorage } from '@lhb/cache';
import { YOUTH_DETAIL_AI_RESULT_CACHE_DATA, SPOT_DETAIL_AI_RESULT_CACHE_DATA, DETAIL_AI_RESULT_CACHE_DATA_COUNT, FromType, YOUTH_PK_DETAIL_AI_RESULT_CACHE_DATA, THINKING_STEPS, THINKING_FOOTER } from './ts-config';
import { deepParseJson } from '@/common/utils/ways';
import cs from 'classnames';
import styles from './index.module.less';
import dayjs from 'dayjs';
import V2Empty from '@/common/components/Data/V2Empty';
import UserInfoContext from '@/layout/context';
import TypewriterText from './TypewriterText';

interface AIProps {
  id: number | number[];
  input: string;
  agentTenant: any;
  isStartAnalyseOfAI: boolean | undefined;
  setIsStartAnalyseOfAI?: any;
  targetCacheAIResult: any;
  type?: number; // 1. 选址地图青春版详情 2. 选址地图青春版PK 3. 点位测评
  extraParams?: any;
  className?: any;
  isAIFailed: boolean;
  setIsAIFailed: (val: boolean) => void;
  // address?: string; // 目标点位名称，type=1和3时传入
};
/**
 * @Description 百炼平台智能体
 */
const AI: FC<AIProps> = ({
  id,
  input,
  type = FromType.youthDetail,
  isStartAnalyseOfAI,
  targetCacheAIResult,
  agentTenant,
  setIsStartAnalyseOfAI,
  className,
  isAIFailed,
  setIsAIFailed,
  // address,
  extraParams,
}) => {
  const { apiKey, agentId } = agentTenant || {};
  const { tenantInfo } = useContext(UserInfoContext);
  const industryName = tenantInfo?.industryName || '';
  const containerRef = useRef<HTMLDivElement>(null);
  const [isCreated, setIsCreated] = useState(false);
  // const [isMCPCall, setIsMCPCall] = useState(false); // 调用MCP服务过程
  // const [thinkingData, setThinkingData] = useState(''); // 分析过程（已注释，改用走马灯）
  const [resultData, setResultData] = useState(''); // 分析的最终结果
  // const tempThinkRef: any = useRef(''); // 思考内容缓存（已注释）
  const resultShowDataRef: any = useRef(''); // 结论
  const pendingResultRef = useRef(''); // 缓存 SSE 返回的结果，等思考动画走完再展示
  const thinkingDoneRef = useRef(false); // ref 版本，供 SSE 回调闭包读取
  const [thinkingDone, setThinkingDone] = useState(false); // 思考动画是否全部完成
  // phase: 0=未开始 1=第1行逐字 2=第2行逐字 3+=步骤列表(phase-3为stepIndex) 末尾=结束语
  const [phase, setPhase] = useState(0);
  const stepTimerRef = useRef<any>(null);

  // 监听 phase 变化，逐字每走完一步自动滚到底
  useEffect(() => {
    if (!containerRef.current) return;
    const dom = containerRef.current;
    requestAnimationFrame(() => {
      dom.scrollTop = dom.scrollHeight;
    });
  }, [phase]);

  useEffect(() => {
    return () => {
      if (stepTimerRef.current) {
        clearInterval(stepTimerRef.current);
        stepTimerRef.current = null;
      }
    };
  }, []);

  // 思考动画走完后，如果 SSE 结果已到，立即展示（SSE 比动画快的场景）
  useEffect(() => {
    if (thinkingDone && pendingResultRef.current) {
      methods.clearStepTimer();
      setResultData(pendingResultRef.current);
    }
  }, [thinkingDone]);

  useEffect(() => {
    // 有结果，点击分析时直接输出缓存结果
    if (targetCacheAIResult?.id && targetCacheAIResult?.data && isStartAnalyseOfAI) {
      setResultData(targetCacheAIResult.data);
      setIsStartAnalyseOfAI && setIsStartAnalyseOfAI(false);
      return;
    }
    // 点了开始分析
    isStartAnalyseOfAI && methods.loadData();
  }, [isStartAnalyseOfAI, targetCacheAIResult]);
  // 特殊处理商圈详情时的flex布局导致的样式问题
  const targetBottom = useMemo(() => {
    return !isStartAnalyseOfAI && !resultData && !isAIFailed && type === FromType.youthDetail;
  }, [isStartAnalyseOfAI, resultData, isAIFailed, type]);
  const targetPromptingQuery = useMemo(() => {
    if (type === FromType.youthDetail) {
      return `正在解析商圈：${extraParams?.areaName}`;
    } else if (type === FromType.spotDetail) { // 点位测评分街边店和商场店
      return `正在分析目标点位：【${extraParams?.areaName || '' || extraParams?.address || ''}】`;
    } else if (type === FromType.youthPKDetail) {
      return `正在对 ${isArray(id) ? (id as number[]).length : 1} 个候选商圈发起横向比对...`;
    }
    return '正在分析...';
  }, [type, id]);
  const methods = useMethods({
    clearStepTimer() {
      if (stepTimerRef.current) {
        clearInterval(stepTimerRef.current);
        stepTimerRef.current = null;
      }
      setPhase(0);
    },
    async loadData() {
      // 重置思考动画相关状态
      pendingResultRef.current = '';
      thinkingDoneRef.current = false;
      setThinkingDone(false);
      // API 文档 https://bailian.console.aliyun.com/cn-beijing/?spm=5176.29597918.J_C-NDPSQ8SFKWB4aef8i6I.2.13f0133cft4Amn&utm_content=se_1021228191&gclid=EAIaIQobChMI1_jvof_PjwMVNCZ7Bx3nEyZpEAAYASAAEgJgcPD_BwE&tab=api#/api/?type=app&url=2981819
      const targetUrl = `https://dashscope.aliyuncs.com/api/v2/apps/agent/${agentId}/compatible-mode/v1/responses`;
      try {
        // if (targetUrl) return;
        const response: any = await fetch(targetUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // 百炼平台要求的认证头
            'Authorization': `Bearer ${apiKey}`,
          },
          body: JSON.stringify({
            input,
            stream: true,
          })
        });

        // 读取流数据
        const reader = response.body.getReader();
        if (!reader) return;

        const decoder = new TextDecoder('utf-8');
        // SSE 事件缓冲区：response.completed 的 data: 行可达数百 KB，
        // 单次 reader.read() 无法完整读取，需要按 \n\n 分隔符累积完整事件后再解析
        let sseBuffer = '';

        const processSSEEvent = (eventText: string) => {
          const lines = eventText.split('\n').filter(line => line.trim() !== '');
          for (const line of lines) {
            // SSE 以 data: 开头
            if (line.startsWith('data:')) {
              const jsonStr = line.slice(5);
              try {
                const data = deepParseJson(jsonStr);
                const { type, delta } = data || {};
                if (type === 'response.created') {
                  setIsCreated(true);
                  setPhase(1); // 从第1行开始逐字展示
                }
                // MCP（已注释，思考过程展示由 phase 控制）
                // if (type === 'response.mcp_call_arguments.done') {
                //   setIsMCPCall(true);
                // }
                // response.reasoning_summary_text.delta 不再累积文本，走马灯已接管思考展示
                // if (type === 'response.reasoning_summary_text.delta') { // 思考过程
                //   const thoughtDelta = delta || '';
                //   tempThinkRef.current += thoughtDelta;
                //   setThinkingData(tempThinkRef.current);
                // }

                // 输出结论
                if (type === 'response.output_text.delta') {
                  resultShowDataRef.current += delta;
                  pendingResultRef.current = resultShowDataRef.current;
                  setIsCreated(false);
                  // setIsMCPCall(false);
                  // 如果思考动画已经走完，立即展示结果（SSE 比动画慢的场景）
                  if (thinkingDoneRef.current) {
                    methods.clearStepTimer();
                    setResultData(resultShowDataRef.current);
                  }
                }

                if (type === 'response.completed') {
                  // console.log(`AI resultShowData`, resultShowDataRef.current);
                  resultShowDataRef.current && methods.handleCacheData({
                    id,
                    data: resultShowDataRef.current
                  });
                }
                if (type === 'response.failed') { // 失败
                  methods.handleFailed();
                }
              } catch (e) {
                // 忽略解析错误
                console.log(`错误信息`, e);
              }
            }
          }
        };

        while (true) {
          const target: any = await reader.read();
          // done只可能是true | false, true: 流已经结束，后端关闭连接，不会再有新数据 false: 流还没结束，读到了一段数据
          const { done, value } = target || {};
          if (done) {
            // 流结束时处理缓冲区中剩余的内容
            if (sseBuffer.trim()) {
              processSSEEvent(sseBuffer);
            }
            break;
          }

          // 解码二进制流并追加到缓冲区
          sseBuffer += decoder.decode(value, { stream: true });

          // 按 SSE 事件分隔符 \n\n 切分，确保每次处理的是完整事件
          const parts = sseBuffer.split('\n\n');
          // 最后一段可能不完整，保留到下次继续拼接
          sseBuffer = parts.pop() || '';

          for (const part of parts) {
            if (part.trim()) {
              processSSEEvent(part);
            }
          }
        }
      } catch (error) {
        console.error('流式请求失败：', error);
        methods.handleFailed();
      } finally {
        // setLoading(false);
      }
    },
    handleFailed() {
      setIsStartAnalyseOfAI && setIsStartAnalyseOfAI(false);
      setResultData('');
      // setThinkingData('');
      methods.clearStepTimer();
      setIsAIFailed(true);
      setIsCreated(false);
      // setIsMCPCall(false);
      pendingResultRef.current = '';
      thinkingDoneRef.current = false;
      setThinkingDone(false);
    },
    // 缓存数据
    handleCacheData(targetCacheData: any) {
      let target = YOUTH_DETAIL_AI_RESULT_CACHE_DATA;
      if (type === FromType.spotDetail) { // 点位测评
        target = SPOT_DETAIL_AI_RESULT_CACHE_DATA;
      }
      if (type === FromType.youthPKDetail) { // 商圈pk
        target = YOUTH_PK_DETAIL_AI_RESULT_CACHE_DATA;
      }
      // 1. 取出原有数据
      let cacheData = getStorage(target);

      if (!cacheData) {
        // 初始化
        cacheData = {
          date: dayjs().format('YYYY-MM-DD'),
          data: []
        };
      }

      // 判断id是否已存在（存在则不添加）
      const isExist = cacheData.data.some(item => {
        if (isArray(id) && id.length && type === FromType.youthPKDetail) {
          return isEqual(item.id.sort((a, b) => a - b), id.sort((a, b) => a - b));
        }
        return item.id === id;
      });
      if (isExist) {
        setIsStartAnalyseOfAI && setIsStartAnalyseOfAI(false);
        return;
      };

      // 推入新数据
      cacheData.data.push(targetCacheData);

      // 超过200条，从 index=0 开始删除
      if (cacheData.data.length > DETAIL_AI_RESULT_CACHE_DATA_COUNT) {
        cacheData.data.shift(); // 删除第一个
      }
      setStorage(target, cacheData);
      setIsStartAnalyseOfAI && setIsStartAnalyseOfAI(false);
    }
  });
  return (
    <div
      className={cs(styles.aiModule, className)}
      style={{
        marginBottom: targetBottom ? '-24px' : ''
      }}>
      {/* 思考过程 */}
      {
        isStartAnalyseOfAI && !isAIFailed && (isCreated || phase > 0) && <>
          {/* 思考过程顶部内容 */}
          <div className={styles.titCon}>
            <div className={styles.complexCon}>
              <div className={styles.starCon}>
                <img src={`${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/ai/icon_ai_thinking_star@2x.png`}
                  width='100%'
                  height='100%'
                />
              </div>
              <div className={styles.dynamicCon}>
                <img src={`${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/ai/icon_ai_thinking_shell@2x.png`}
                  width='100%'
                  height='100%'
                />
              </div>
            </div>
            <div className='c-222 fs-16 ff-semibold fw-600 ml-8'>正在深度思考中</div>
          </div>
          {/* 逐字展示内容区 */}
          <div ref={containerRef} className={cs(styles.aiCon, styles.aiConThinking, type === FromType.spotDetail ? 'mb-12' : '')}>
            {/* MCP 提示已注释 */}
            {/* {
              isMCPCall && <div>开始调用MCP服务...</div>
            } */}
            {/* 第1行：顾问介绍 */}
            {phase >= 1 && (
              <div className='fs-12 c-666 mt-6'>
                <TypewriterText
                  text={`我是一名专业的${industryName}门店选址顾问。`}
                  onDone={() => setPhase(p => p === 1 ? 2 : p)}
                />
              </div>
            )}
            {/* 第2行：目标信息 */}
            {phase >= 2 && (
              <div className='fs-12 c-666 mb-8 mt-6'>
                <TypewriterText
                  text={targetPromptingQuery}
                  onDone={() => setPhase(p => p === 2 ? 3 : p)}
                />
              </div>
            )}
            {/* 步骤列表：phase=3 起，每完成一条 phase+1 */}
            {phase >= 3 && (
              <div>
                {(THINKING_STEPS[type] || []).map((step, i) => {
                  if (phase < i + 3) return null;
                  return (
                    <div key={i} className={styles.stepItem}>
                      <div className={styles.stepDot} />
                      <TypewriterText
                        text={step}
                        onDone={() => setPhase(p => p === i + 3 ? i + 4 : p)}
                      />
                    </div>
                  );
                })}
              </div>
            )}
            {/* 结束语：所有步骤完成后 */}
            {phase >= (THINKING_STEPS[type] || []).length + 3 && (
              <div className='fs-12 c-666 mt-6'>
                <TypewriterText text={THINKING_FOOTER[type]} onDone={() => {
                  setPhase((state) => state + 1);
                  thinkingDoneRef.current = true;
                  setThinkingDone(true);
                }}/>
              </div>
            )}
          </div>
        </>
      }

      {/* 回答的结果 */}
      {
        resultData && <>
          <div className={styles.resultTitCon}>
            <div className={styles.hintCon}>
              <div>
                <img src={`${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/ai/icon_ai_res@2x.png`}
                  width='16px'
                  height='16px'
                />
              </div>
              <div className='c-222 fs-16 ff-semibold fw-600 ml-8'>已深度思考</div>
            </div>
            <div className='fs-12 c-999'>
            内容由AI生成，仅供参考
            </div>
          </div>
          <div className={cs(styles.aiCon, styles.aiConResult, type === FromType.spotDetail ? 'mb-12' : '')}>
            {resultData}
          </div>
        </>
      }
      {/* 分析出错 */}
      {
        isAIFailed && <V2Empty customTip='出错了' className={type === FromType.spotDetail ? 'mb-10' : ''}/>
      }
    </div>
  );
};

export default AI;
